import React, {useEffect, useState} from "react";
import {useTheme} from "../../../theme/ThemeProvider";
import {buildStyles} from "../../buildStyles";
import Container from "../../layout/Container";
import FlexContainer from "../../layout/FlexContainer";
import SvgIcon from "../SvgIcon";
import ICONS from "connectui/icons/iconMap";
import ButtonIcon from "../../input/button/ButtonIcon";
import Text from "../Text";
import PropTypes from "prop-types";

const setupAnimations = () => {
  if (
    typeof document !== "undefined" &&
    !document.getElementById("carousel-animations")
  ) {
    const styleSheet = document.createElement("style");
    styleSheet.id = "carousel-animations";
    styleSheet.textContent = `
      @keyframes slideInRight {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideInLeft {
        from { transform: translateX(-100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideOutRight {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(100%); opacity: 0; }
      }
      @keyframes slideOutLeft {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(-100%); opacity: 0; }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes fadeOut {
        from { opacity: 1; }
        to { opacity: 0; }
      }
    `;
    document.head.appendChild(styleSheet);
  }
};

const useStyles = (theme) =>
  buildStyles({
    root: {
      position: "relative",
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    imageContainer: {
      position: "relative",
      width: "100%",
      height: "100%",
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      borderRadius: theme.radius.md,
    },
    slideBase: {
      position: "absolute",
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "none",
      transition: "transform 300ms ease, opacity 300ms ease",
      padding: 0,
    },
    image: {
      width: "auto",
      height: "auto",
      maxWidth: "100%",
      maxHeight: "100%",
      objectFit: "contain",
    },

    controls: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: theme.spacing(1),
      pointerEvents: "none",
    },
    navButton: {
      backgroundColor: `${theme.colors.white}99`,
      borderRadius: "50%",
      width: "32px",
      height: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "none",
      cursor: "pointer",
      boxShadow: theme.shadows.sm,
      pointerEvents: "auto",
      margin: theme.spacing(0, 1),
      "&:hover": {
        backgroundColor: theme.colors.white,
      },
    },
    indicators: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      width: "100%",
    },
    indicator: {
      width: theme.spacing(2),
      height: theme.spacing(2),
      borderRadius: "50%",
      backgroundColor: theme.colors.gray300,
      cursor: "pointer",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: theme.colors.gray500,
      },
    },
    activeIndicator: {
      backgroundColor: theme.colors.primary500,
      width: theme.spacing(2),
      "&:hover": {
        backgroundColor: theme.colors.primary600,
      },
    },
    caption: {
      textAlign: "center",
      marginTop: theme.spacing(1),
      color: theme.colors.textSecondary,
      fontSize: theme.typography.textSM.fontSize,
      fontWeight: theme.typography.medium,
    },
  });

const ImageCarousel = ({
  images = [],
  initialIndex = 0,
  showControls = true,
  sx = {},
  onSlideChange,
  ...props
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [prevIndex, setPrevIndex] = useState(null);
  const [direction, setDirection] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (onSlideChange && typeof onSlideChange === "function") {
      onSlideChange(currentIndex);
    }
  }, [currentIndex, onSlideChange]);

  useEffect(() => {
    setupAnimations();
  }, []);

  if (!images.length) {
    return null;
  }

  const handleNext = () => {
    if (isAnimating) return;
    setPrevIndex(currentIndex);
    setDirection("right");
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }, 10);
  };

  const handlePrev = () => {
    if (isAnimating) return;
    setPrevIndex(currentIndex);
    setDirection("left");
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex(
        (prevIndex) => (prevIndex - 1 + images.length) % images.length,
      );
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }, 10);
  };

  const handleIndicatorClick = (index) => {
    if (isAnimating || index === currentIndex) return;
    setPrevIndex(currentIndex);
    setDirection(index > currentIndex ? "right" : "left");
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }, 10);
  };

  const getSlideStyle = (isVisible, isEntering) => {
    if (!isVisible) return {display: "none"};

    if (!isAnimating) {
      return {className: classes.slideBase};
    }

    if (isEntering) {
      const animationName =
        direction === "right" ? "slideInRight" : "slideInLeft";
      return {
        className: classes.slideBase,
        style: {animation: `${animationName} 300ms forwards`},
      };
    } else {
      const animationName =
        direction === "right" ? "slideOutLeft" : "slideOutRight";
      return {
        className: classes.slideBase,
        style: {animation: `${animationName} 300ms forwards`},
      };
    }
  };

  return (
    <Container className={classes.root} sx={sx} {...props}>
      <Container className={classes.imageContainer}>
        {isAnimating && prevIndex !== null && (
          <Container
            className={getSlideStyle(true, false).className}
            sx={getSlideStyle(true, false).style}
          >
            {images[prevIndex]?.url && (
              <img
                src={images[prevIndex].url}
                alt={images[prevIndex].alt || `Image ${prevIndex + 1}`}
                className={classes.image}
              />
            )}
          </Container>
        )}

        <Container
          className={getSlideStyle(true, isAnimating).className}
          sx={getSlideStyle(true, isAnimating).style}
        >
          {images[currentIndex]?.url && (
            <img
              src={images[currentIndex].url}
              alt={images[currentIndex].alt || `Image ${currentIndex + 1}`}
              className={classes.image}
            />
          )}
        </Container>
      </Container>

      {showControls && images.length > 1 && (
        <Container className={classes.controls}>
          <ButtonIcon
            className={classes.navButton}
            onClick={handlePrev}
            icon={
              <SvgIcon
                component={ICONS["chevron-left"]}
                sx={{width: "20px", height: "20px"}}
              />
            }
            disabled={isAnimating}
          />
          <ButtonIcon
            className={classes.navButton}
            onClick={handleNext}
            icon={
              <SvgIcon
                component={ICONS["chevron-right"]}
                sx={{width: "20px", height: "20px"}}
              />
            }
            disabled={isAnimating}
          />
        </Container>
      )}

      {images.length > 1 && (
        <FlexContainer
          className={classes.indicators}
          gap={theme.spacing(2)}
          justifyContent="center"
          alignItems="center"
        >
          {images.map((_, index) => (
            <Container
              key={index}
              className={`${classes.indicator} ${index === currentIndex ? classes.activeIndicator : ""}`}
              onClick={() => handleIndicatorClick(index)}
            />
          ))}
        </FlexContainer>
      )}

      {images[currentIndex]?.alt && images.length <= 1 && (
        <FlexContainer
          justifyContent="center"
          alignItems="center"
          gap={theme.spacing(1)}
          sx={{marginTop: theme.spacing(2)}}
        >
          <Text variant="textSM" weight="medium" className={classes.caption}>
            {images[currentIndex].alt}
          </Text>
          {images.length > 1 && (
            <Text variant="textXS" color="textTertiary">
              ({currentIndex + 1}/{images.length})
            </Text>
          )}
        </FlexContainer>
      )}
    </Container>
  );
};

ImageCarousel.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      alt: PropTypes.string,
    }),
  ).isRequired,
  initialIndex: PropTypes.number,
  showControls: PropTypes.bool,
  sx: PropTypes.object,
  onSlideChange: PropTypes.func,
};

export default ImageCarousel;
